/**
 * Fluxo de caixa (E2 realizado; E3 projetado; E4 fotos). Cada parte respeita a permissão da sua origem; o que o perfil
 * não vê fica fora como "consolidado parcial", sem valores, nomes ou quantidades da parte sem acesso.
 *  - Realizado: pelo extrato (cobertura efetiva das importações ativas). Cada movimento conta uma vez, classificado pela
 *    conciliação; sem classificação, em trânsito e aguardando extrato à parte; saldo provisório a partir da primeira
 *    lacuna; conferência com o saldo do extrato mostra a diferença sem presumir a causa; aplicações e resgates em linhas
 *    próprias; posição investida separada do caixa disponível. Mensal ou diário; consolidado ou por conta; detalhe até as
 *    origens (movimento → conciliação → pagamento/recebimento/evento).
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { api } from '../api.ts';
import { useRota, navegar } from '../rotas.ts';
import { Abas, Ajuda, CardLista, CardsLista, Codigo, ComCodigos, EstadoCarga, Modal, RolagemIndicada, SeloSituacao, useCarga } from '../comercial/ui.tsx';
import { useSessao } from '../sessao.tsx';
import { ReprogramarVencimento } from './Reprogramar.tsx';
import { Campo } from '../componentes/basicos.tsx';
import { dataBr, mesExtenso, plural, rs } from './ContasPagar.tsx';

const c$ = (v: string | null | undefined) => Math.round(Number(v ?? 0) * 100);
const hojeIso = () => new Date().toLocaleDateString('sv-SE', { timeZone: 'America/Sao_Paulo' });
const somarDias = (iso: string, k: number) => new Date(Date.parse(`${iso}T00:00:00Z`) + k * 86_400_000).toISOString().slice(0, 10);
const somarMeses = (mes: string, k: number) => {
  const [a, m] = mes.split('-').map(Number);
  const d = new Date(Date.UTC(a, m - 1 + k, 1));
  return d.toISOString().slice(0, 7);
};
const ultimoDia = (mes: string) => {
  const [a, m] = mes.split('-').map(Number);
  return new Date(Date.UTC(a, m, 0)).toISOString().slice(0, 10);
};
const rotuloPeriodo = (chave: string) => (chave.length === 7 ? mesExtenso(chave) : dataBr(chave).slice(0, 5));
/** colunas de período por página (cabem sem rolagem lateral em 1024 e 1440) */
function useColunas() {
  const calc = () => (window.innerWidth >= 1360 ? 6 : window.innerWidth >= 1180 ? 5 : 4);
  const [n, setN] = useState(calc);
  useEffect(() => {
    const ao = () => setN(calc());
    window.addEventListener('resize', ao);
    return () => window.removeEventListener('resize', ao);
  }, []);
  return n;
}

export type Grupo = 'entrada' | 'saida' | 'aplicacao' | 'resgate' | 'transferencia' | 'transito';
export const ROTULO_GRUPO: Record<Grupo, string> = {
  entrada: 'Entradas',
  saida: 'Saídas',
  aplicacao: 'Aplicações',
  resgate: 'Resgates',
  transferencia: 'Transferências entre contas próprias',
  transito: 'Em trânsito',
};
const AJUDA_GRUPO: Record<Grupo, string> = {
  entrada: 'Dinheiro que entrou na conta, pela conciliação de cada movimento. “A identificar”: sem classificação ainda.',
  saida: 'Dinheiro que saiu da conta, pela conta gerencial do pagamento conciliado. “Sem conciliação”: sem classificação ainda.',
  aplicacao: 'Saídas para aplicação (RDB): não são despesa; vão para a posição investida.',
  resgate: 'Voltas da aplicação para a conta (líquido creditado): não são receita só por isso.',
  transferencia: 'Entre contas da própria Inttrix: não são receita nem despesa; no consolidado as duas pontas se anulam.',
  transito: 'Transferência com uma ponta só no extrato (a outra conta ainda não mostrou): fica em trânsito.',
};

interface Item { movimento: string; conta: string; data: string; valor: string; descricao: string; conciliacao: string | null; origens: string[]; sugestao: string | null; fonte: 'extrato' | 'hub_lacuna' }
interface Linha { chave: string; grupo: Grupo; rotulo: string; semClassificacao: boolean; restrito: boolean; porPeriodo: Record<string, string>; provisorioPorPeriodo: Record<string, string>; porConta: Record<string, string>; total: string; itens: Item[] }
interface PeriodoSaldo { chave: string; de?: string; ate?: string; saldoInicial: string | null; saldoFinal: string | null; soImportado?: string | null; provisorio: boolean; movimento: string; fatosNaLacuna?: string; posicaoInvestida: string | null; posicaoProvisoria: boolean; contasSemSaldo?: number; diasCobertos?: number; diasNoPeriodo?: number }
interface Cronologia { data: string; fonte: 'saldo_inicial' | 'extrato' | 'saldo_observado' | 'hub_provisorio' | 'hub' | 'saldo_realizado'; registro: string; valor: string | null; situacao: string; tom: 'conferido' | 'conciliado' | 'pendente' | 'provisorio' | 'divergencia' | 'sugestao'; foraDosTotais?: boolean }
interface ContaRealizado {
  conta: { id: string; rotulo: string; banco: string; agencia: string; numero: string };
  saldoInicial: { valor: string; data: string } | null;
  cobertura: { cobertos: { de: string; ate: string }[]; lacunas: { de: string; ate: string }[]; primeiraLacuna: string | null; conferidoAte: string | null; ate: string | null; canceladas: { numero: string; de: string; ate: string }[]; importacoes: { numero: string; de: string; ate: string; parcial: boolean }[] };
  periodos: PeriodoSaldo[];
  conferencias: { data: string; extrato: string; calculado: string | null; diferenca: string | null; importacao: string; antesDaLacuna: boolean; fatosNaLacuna: string }[];
  cronologia: Cronologia[] | null;
}
interface FatoHub { tipo: string; numero: string; conta: string; data: string; valor: string; origem: string | null; situacao: 'na_lacuna' | 'aguardando_extrato' | 'a_confirmar' | 'divergencia'; sugestao: string | null }
interface Realizado {
  de: string;
  ate: string;
  hoje: string;
  granularidade: 'dia' | 'mes';
  acesso: { conciliacao: boolean; pagar: boolean; receber: boolean; parcial: boolean };
  indisponivel?: string;
  contasDisponiveis: { id: string; banco: string; agencia: string; numero: string }[];
  totais?: { entradas: string; saidas: string; aplicacoes: string; resgates: string; transferencias: string; emTransito: string; semClassificacao: string; itensSemClassificacao: number; fatosNaLacuna: number; aConfirmar: number; divergencias: number; diferencasSaldo: number; lacunas: number };
  linhas?: Linha[];
  periodos?: PeriodoSaldo[];
  contas?: ContaRealizado[];
  fatosHub?: FatoHub[];
}

const ROTULO_AGUARDANDO: Record<string, string> = { pagamento: 'Pagamento', recebimento: 'Recebimento', recuperacao: 'Recuperação', devolucao_credito: 'Devolução de crédito', aplicacao: 'Aplicação', resgate: 'Resgate' };

/** Aviso único de consolidado parcial: não revela o que falta (nem valores, nomes ou quantidades). */
export function AvisoParcial({ texto }: { texto?: string }) {
  return (
    <p className="aviso aviso-atencao-fin" data-teste="consolidado-parcial">
      <strong>Consolidado parcial.</strong> {texto ?? 'Parte das origens do Fluxo não é visível para o seu perfil; o que aparece aqui é só o que você pode ver.'}
    </p>
  );
}

/* ---------------------------------------------------------------- tela */
export function TelaFluxoCaixa() {
  const rota = useRota();
  const parte = rota.split('/')[2];
  const aba: 'realizado' | 'projetado' | 'fotos' = parte === 'projetado' ? 'projetado' : parte === 'fotos' ? 'fotos' : 'realizado';
  return (
    <>
      <h1 className="titulo-pagina rotulo-oculto">Fluxo de caixa</h1>
      <Abas
        rotulo="Fluxo de caixa"
        atual={aba}
        aoTrocar={(a) => navegar(`/fluxo-caixa/${a}`)}
        abas={[
          { id: 'realizado', rotulo: 'Realizado' },
          { id: 'projetado', rotulo: 'Projeção atual' },
          { id: 'fotos', rotulo: 'Fotos da projeção' },
        ]}
      />
      {aba === 'realizado' ? <AbaRealizado /> : aba === 'projetado' ? <AbaProjetado /> : <AbaFotos />}
    </>
  );
}

function AbaRealizado() {
  const hoje = hojeIso();
  const [granularidade, setGranularidade] = useState<'mes' | 'dia'>('mes');
  const [mesIni, setMesIni] = useState(somarMeses(hoje.slice(0, 7), -5));
  const [mesFim, setMesFim] = useState(hoje.slice(0, 7));
  const [diaIni, setDiaIni] = useState(`${hoje.slice(0, 7)}-01`);
  const [diaFim, setDiaFim] = useState(hoje);
  const [conta, setConta] = useState('');
  const de = granularidade === 'mes' ? `${mesIni}-01` : diaIni;
  const ate = granularidade === 'mes' ? (ultimoDia(mesFim) > hoje && mesFim === hoje.slice(0, 7) ? hoje : ultimoDia(mesFim)) : diaFim;
  const carga = useCarga(() => api.get<Realizado>(`/api/fluxo-caixa/realizado?de=${de}&ate=${ate}&granularidade=${granularidade}${conta ? `&conta=${conta}` : ''}`), [de, ate, granularidade, conta]);
  const d = carga.dados;
  const [aberta, setAberta] = useState<{ linha: string; periodo: string | null } | null>(null);
  useEffect(() => setAberta(null), [de, ate, granularidade, conta]);
  const filtros = (
    <div className="barra-filtros conc-barra" data-teste="filtros-fluxo">
      <label>
        Visão
        <select value={granularidade} onChange={(e) => setGranularidade(e.target.value as 'mes' | 'dia')} data-teste="granularidade-fluxo">
          <option value="mes">Mensal</option>
          <option value="dia">Diária</option>
        </select>
      </label>
      {granularidade === 'mes' ? (
        <>
          <label>
            De
            <input type="month" value={mesIni} max={mesFim} onChange={(e) => e.target.value && setMesIni(e.target.value)} data-teste="mes-ini-fluxo" />
          </label>
          <label>
            Até
            <input type="month" value={mesFim} min={mesIni} onChange={(e) => e.target.value && setMesFim(e.target.value)} data-teste="mes-fim-fluxo" />
          </label>
        </>
      ) : (
        <>
          <label>
            De
            <input type="date" value={diaIni} max={diaFim} onChange={(e) => e.target.value && setDiaIni(e.target.value)} data-teste="dia-ini-fluxo" />
          </label>
          <label>
            Até
            <input type="date" value={diaFim} min={diaIni} onChange={(e) => e.target.value && setDiaFim(e.target.value)} data-teste="dia-fim-fluxo" />
          </label>
        </>
      )}
      <label>
        Conta
        <select value={conta} onChange={(e) => setConta(e.target.value)} data-teste="conta-fluxo">
          <option value="">Consolidado (todas as contas)</option>
          {(d?.contasDisponiveis ?? []).map((c) => (
            <option key={c.id} value={c.id}>
              {c.banco} · ag. {c.agencia} · conta {c.numero}
            </option>
          ))}
        </select>
      </label>
    </div>
  );
  if (!d) return (
    <>
      {filtros}
      <EstadoCarga carregando={carga.carregando} erro={carga.erro} recarregar={carga.recarregar} teste="fluxo-realizado" />
    </>
  );
  if (d.indisponivel)
    return (
      <>
        <AvisoParcial texto={d.indisponivel} />
      </>
    );
  const t = d.totais!;
  const ultimo = d.periodos!.at(-1)!;
  const aoVerConta = (id: string) => setConta(id);
  const umaConta = d.contas!.length === 1 ? d.contas![0] : null;
  const linhaAberta = aberta && d.linhas!.find((l) => l.chave === aberta.linha);
  return (
    <>
      {filtros}
      {d.acesso.parcial && <AvisoParcial />}
      <CardsLista n={4} teste="cards-fluxo-realizado">
        <CardLista
          icone="dinheiro"
          tom="azul"
          rotulo="Caixa disponível (saldo em conta)"
          valor={ultimo.saldoFinal === null ? '—' : rs(ultimo.saldoFinal)}
          detalhe={`em ${dataBr(ultimo.ate ?? d.ate)}${ultimo.provisorio ? ' · provisório' : ''}${ultimo.soImportado && ultimo.soImportado !== ultimo.saldoFinal ? ` · só com o importado ${rs(ultimo.soImportado)}` : ''}${ultimo.contasSemSaldo ? ` · ${plural(ultimo.contasSemSaldo, 'conta sem saldo inicial', 'contas sem saldo inicial')}` : ''}`}
          ajuda={<Ajuda titulo="Caixa disponível">Saldo inicial da conta + movimentos ATIVOS do extrato + fatos do HUB nos dias de lacuna (provisórios). É conferido só até a primeira lacuna; depois, provisório, mesmo que coincida com o banco. Não inclui a posição investida.</Ajuda>}
          teste="card-caixa"
        />
        <CardLista
          icone="relogio"
          tom="roxo"
          rotulo="Posição investida (separada)"
          valor={ultimo.posicaoInvestida === null ? '—' : rs(ultimo.posicaoInvestida)}
          detalhe={ultimo.posicaoInvestida === null ? 'sem aplicações' : `principal calculado${ultimo.posicaoProvisoria ? ' · provisório (resgate a informar)' : ''}`}
          ajuda={<Ajuda titulo="Posição investida">Principal das aplicações (RDB) no fim do período, calculado pelos eventos registrados em Aplicações. Fica separado do caixa disponível: não é dinheiro em conta.</Ajuda>}
          teste="card-investida"
        />
        <CardLista
          icone="documento"
          tom="verde"
          rotulo="Entradas · saídas do período"
          valor={`${rs(t.entradas)} · ${rs(Math.abs(c$(t.saidas)) / 100)}`}
          detalhe={`aplicações ${rs(Math.abs(c$(t.aplicacoes)) / 100)} · resgates ${rs(t.resgates)}${c$(t.emTransito) ? ` · em trânsito ${rs(t.emTransito)}` : ''}`}
          teste="card-entradas-saidas"
        />
        <CardLista
          icone="alerta"
          tom="vermelho"
          rotulo="Para conferir"
          valor={plural(t.itensSemClassificacao, 'sem classificação', 'sem classificação')}
          detalhe={`${plural(t.lacunas, 'lacuna', 'lacunas')} (${plural(t.fatosNaLacuna, 'fato do HUB provisório', 'fatos do HUB provisórios')}) · ${plural(t.aConfirmar, 'a confirmar', 'a confirmar')} · ${plural(t.divergencias, 'divergência', 'divergências')} · ${plural(t.diferencasSaldo, 'diferença de saldo', 'diferenças de saldo')}`}
          ajuda={<Ajuda titulo="Para conferir">Sem classificação: movimentos do extrato ainda não conciliados. Lacuna: dias sem extrato importado — os fatos registrados no HUB nesses dias entram provisoriamente. Divergência: registrado no HUB num dia que o extrato cobre e não mostra (fora dos totais). Diferença de saldo: saldo do extrato × calculado na mesma data — mostrada sem presumir a causa.</Ajuda>}
          teste="card-conferir"
        />
      </CardsLista>
      <MatrizFluxo
        periodos={d.periodos!}
        linhas={d.linhas!}
        aoAbrir={(linha, periodo) => setAberta({ linha, periodo })}
        teste="matriz-realizado"
      />
      {linhaAberta && <DetalheLinha linha={linhaAberta} periodo={aberta!.periodo} aoFechar={() => setAberta(null)} />}
      <h2 className="conc-titulo">
        Fatos do HUB sem extrato
        <Ajuda titulo="Fatos do HUB sem extrato">Registrado no HUB (pagamento, recebimento, recuperação, devolução, aplicação ou resgate) e ainda sem conciliação. “Aguardando extrato”: a data é posterior ao último extrato importado; “Na lacuna · sem extrato”: a data está num intervalo sem extrato entre dois importados — nos dois casos entra no realizado provisoriamente. “A confirmar”: o extrato cobre a data e mostra um movimento de igual valor ainda não conciliado (candidato). “Divergência”: o extrato cobre a data e não mostra movimento de igual valor. Os dois ficam fora dos totais. Quando a lacuna é importada, aqueles dias passam a usar só o banco. Correspondência provável é só “a confirmar”.</Ajuda>
      </h2>
      {d.fatosHub!.length ? (
        <RolagemIndicada className="rolagem quadro-tabela">
          <table className="tabela tabela-compacta" data-teste="tabela-fatos-hub">
            <thead>
              <tr>
                <th>Data</th>
                <th>Registro</th>
                <th>Conta</th>
                <th className="numero">Valor</th>
                <th>Situação</th>
              </tr>
            </thead>
            <tbody>
              {d.fatosHub!.map((a) => (
                <tr key={a.numero} className={a.situacao === 'divergencia' || a.situacao === 'a_confirmar' ? 'fluxo-fora' : undefined} data-teste={`fato-${a.numero}`}>
                  <td className="conc-nowrap">{dataBr(a.data)}</td>
                  <td>
                    {ROTULO_AGUARDANDO[a.tipo] ?? a.tipo} <ComCodigos texto={a.numero} />
                    {a.origem && (
                      <div className="texto-secundario conc-mini">
                        <ComCodigos texto={a.origem} />
                      </div>
                    )}
                  </td>
                  <td className="texto-secundario conc-mini">{a.conta}</td>
                  <td className="numero conc-nowrap">{a.situacao === 'divergencia' || a.situacao === 'a_confirmar' ? `(${rs(a.valor)})` : rs(a.valor)}</td>
                  <td>
                    <SeloSituacao texto={a.situacao === 'na_lacuna' ? 'Na lacuna · sem extrato' : a.situacao === 'aguardando_extrato' ? 'Aguardando extrato' : a.situacao === 'a_confirmar' ? 'A confirmar · fora dos totais' : 'Divergência · fora dos totais'} tom={a.situacao === 'divergencia' ? 'alerta' : 'parcial'} />
                    {a.sugestao && <div className="texto-secundario conc-mini">{a.sugestao}</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </RolagemIndicada>
      ) : (
        <p className="texto-secundario" data-teste="sem-fatos-hub">Nenhum fato do HUB sem extrato neste período{d.acesso.parcial ? ' (entre o que o seu perfil vê)' : ''}.</p>
      )}
      {umaConta?.cronologia && <CronologiaConta conta={umaConta} />}
      <h2 className="conc-titulo">
        Cobertura do extrato e conferência do saldo
        <Ajuda titulo="Cobertura">Por conta: os períodos cobertos pelas importações ativas (canceladas não contam; parciais cobrem só o seu intervalo) e as lacunas. Importar a lacuna depois completa a cobertura sem duplicar movimentos. A conferência compara o saldo informado pelo banco com o calculado na mesma data.</Ajuda>
      </h2>
      <ul className="conc-lista" data-teste="cobertura-contas">
        {d.contas!.map((c) => (
          <li key={c.conta.id} data-teste={`cobertura-${c.conta.numero}`}>
            <strong>{c.conta.rotulo}</strong>
            <div className="texto-secundario">
              {c.saldoInicial ? `saldo inicial ${rs(c.saldoInicial.valor)} em ${dataBr(c.saldoInicial.data)}` : 'sem saldo inicial: saldo não calculado'} ·{' '}
              {c.cobertura.cobertos.length ? `coberto ${c.cobertura.cobertos.map((x) => `${dataBr(x.de)}–${dataBr(x.ate)}`).join(', ')}` : 'nenhum extrato no período'}
              {c.cobertura.importacoes.some((i) => i.parcial) && ` · ${plural(c.cobertura.importacoes.filter((i) => i.parcial).length, 'importação parcial', 'importações parciais')}`}
              {c.cobertura.canceladas.length > 0 && ` · ${c.cobertura.canceladas.map((x) => `${x.numero} (${dataBr(x.de).slice(0, 5)}–${dataBr(x.ate).slice(0, 5)}) cancelada: não cobre`).join(', ')}`}
              {c.cobertura.conferidoAte && ` · saldo conferido só até ${dataBr(c.cobertura.conferidoAte)}`}
              {(() => {
                const dif = c.conferencias.find((f) => f.antesDaLacuna && f.diferenca !== null && c$(f.diferenca) !== 0);
                return dif ? ` · em ${dataBr(dif.data)} o saldo do banco difere do calculado em ${rs(dif.diferenca!)} (sem causa presumida)` : '';
              })()}
            </div>
            {c.cobertura.lacunas.length > 0 && (
              <div data-teste={`lacunas-${c.conta.numero}`}>
                <SeloSituacao texto="Lacuna" tom="alerta" /> {c.cobertura.lacunas.map((x) => (x.de === x.ate ? dataBr(x.de) : `${dataBr(x.de)} a ${dataBr(x.ate)}`)).join(' · ')} — saldo provisório a partir de {dataBr(c.cobertura.primeiraLacuna)}
              </div>
            )}
            {!c.cronologia && (
              <button type="button" className="botao-link" onClick={() => aoVerConta(c.conta.id)} data-teste={`ver-cobertura-${c.conta.numero}`}>
                Ver a cronologia da conta
              </button>
            )}
            {c.conferencias.map((f) => (
              <div key={`${f.importacao}-${f.data}`} className="conc-mini" data-teste={`conferencia-${c.conta.numero}-${f.data}`}>
                {dataBr(f.data)}: extrato {rs(f.extrato)} × calculado só com o importado {f.calculado === null ? '—' : rs(f.calculado)}{' '}
                {f.diferenca === null ? '' : !f.antesDaLacuna ? (
                  <span className="texto-secundario">
                    provisório (depois da lacuna){c$(f.diferenca) ? ` · diferença ${rs(f.diferenca)} sem causa presumida` : ''}
                    {c$(f.fatosNaLacuna) ? ` · fatos do HUB na lacuna ${rs(f.fatosNaLacuna)} (coincidir não prova nada)` : ''}
                  </span>
                ) : c$(f.diferenca) === 0 ? (
                  <span className="conc-ok">conferido ✓</span>
                ) : (
                  <span className="conc-dif">diferença {rs(f.diferenca)} (sem causa presumida)</span>
                )}{' '}
                · <Codigo>{f.importacao}</Codigo>
              </div>
            ))}
          </li>
        ))}
      </ul>
    </>
  );
}

/* ---------------------------------------------------------------- matriz (períodos em páginas de colunas) */
export interface LinhaMatriz { chave: string; grupo: Grupo; rotulo: string; porPeriodo: Record<string, string>; total: string; semClassificacao?: boolean; restrito?: boolean; marca?: ReactNode; provisorioPorPeriodo?: Record<string, string>; rotuloProvisorio?: string }
export function MatrizFluxo({ periodos, linhas, aoAbrir, teste, rodape, rotuloSaldo, semInvestida }: { periodos: PeriodoSaldo[]; linhas: LinhaMatriz[]; aoAbrir?: (linha: string, periodo: string | null) => void; teste: string; rodape?: (p: PeriodoSaldo) => ReactNode; rotuloSaldo?: string; semInvestida?: boolean }) {
  const n = useColunas();
  const [paginaEscolhida, setPagina] = useState(() => Math.max(0, Math.ceil(periodos.length / n) - 1));
  const paginas = Math.max(1, Math.ceil(periodos.length / n));
  useEffect(() => setPagina(Math.max(0, Math.ceil(periodos.length / n) - 1)), [periodos.length, n]);
  // ao alargar a janela (mais colunas, menos páginas) a página escolhida pode deixar de existir antes do efeito acima
  const pagina = Math.min(paginaEscolhida, paginas - 1);
  const vis = periodos.slice(pagina * n, pagina * n + n);
  const grupos = useMemo(() => {
    const ordem: Grupo[] = ['entrada', 'saida', 'aplicacao', 'resgate', 'transferencia', 'transito'];
    return ordem.map((g) => ({ g, linhas: linhas.filter((l) => l.grupo === g) })).filter((x) => x.linhas.length);
  }, [linhas]);
  const soma = (ls: LinhaMatriz[], k: string) => ls.reduce((s, l) => s + c$(l.porPeriodo[k]), 0);
  return (
    <>
      {paginas > 1 && (
        <div className="fluxo-paginas" data-teste={`${teste}-paginas`}>
          <button type="button" className="botao-secundario pequeno" disabled={pagina === 0} onClick={() => setPagina(pagina - 1)} data-teste={`${teste}-anteriores`}>
            ‹ Anteriores
          </button>
          <span className="texto-secundario">
            {rotuloPeriodo(vis[0].chave)} a {rotuloPeriodo(vis.at(-1)!.chave)} · {pagina + 1} de {paginas}
          </span>
          <button type="button" className="botao-secundario pequeno" disabled={pagina >= paginas - 1} onClick={() => setPagina(pagina + 1)} data-teste={`${teste}-proximos`}>
            Próximos ›
          </button>
        </div>
      )}
      <RolagemIndicada className="rolagem quadro-tabela">
        <table className="tabela tabela-compacta fluxo-matriz" data-teste={teste}>
          <thead>
            <tr>
              <th>Linha</th>
              {vis.map((p) => (
                <th key={p.chave} className="numero">
                  {rotuloPeriodo(p.chave)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr className="fluxo-saldo">
              <td>Saldo inicial</td>
              {vis.map((p) => (
                <td key={p.chave} className="numero">
                  {p.saldoInicial === null ? '—' : rs(p.saldoInicial)}
                </td>
              ))}
            </tr>
            {grupos.map(({ g, linhas: ls }) => (
              <GrupoLinhas key={g} grupo={g} linhas={ls} vis={vis} soma={soma} aoAbrir={aoAbrir} teste={teste} />
            ))}
            <tr className="fluxo-saldo" data-teste={`${teste}-saldo-final`}>
              <td>
                {rotuloSaldo ?? 'Saldo final (caixa disponível)'}
                <Ajuda titulo="Saldo final">“provisório”: há dia sem extrato importado desde o saldo inicial (ou ainda não chegou ao fim do período). A posição investida fica na linha de baixo, separada.</Ajuda>
              </td>
              {vis.map((p) => (
                <td key={p.chave} className="numero" data-teste={`${teste}-saldo-${p.chave}`}>
                  {p.saldoFinal === null ? '—' : rs(p.saldoFinal)}
                  {p.provisorio && <div className="texto-secundario conc-mini">provisório</div>}
                  {rodape?.(p)}
                </td>
              ))}
            </tr>
            {!semInvestida && <tr className="fluxo-investida">
              <td>Posição investida (fora do caixa)</td>
              {vis.map((p) => (
                <td key={p.chave} className="numero">
                  {p.posicaoInvestida === null ? '—' : rs(p.posicaoInvestida)}
                  {p.posicaoProvisoria && <div className="texto-secundario conc-mini">provisória</div>}
                </td>
              ))}
            </tr>}
          </tbody>
        </table>
      </RolagemIndicada>
    </>
  );
}

function GrupoLinhas({ grupo, linhas, vis, soma, aoAbrir, teste }: { grupo: Grupo; linhas: LinhaMatriz[]; vis: PeriodoSaldo[]; soma: (ls: LinhaMatriz[], k: string) => number; aoAbrir?: (linha: string, periodo: string | null) => void; teste: string }) {
  const [aberto, setAberto] = useState(true);
  return (
    <>
      <tr className="fluxo-grupo" data-teste={`${teste}-grupo-${grupo}`}>
        <td>
          <button type="button" className="botao-link" aria-expanded={aberto} onClick={() => setAberto(!aberto)}>
            {aberto ? '▾' : '▸'} {ROTULO_GRUPO[grupo]}
          </button>
          <Ajuda titulo={ROTULO_GRUPO[grupo]}>{AJUDA_GRUPO[grupo]}</Ajuda>
        </td>
        {vis.map((p) => (
          <td key={p.chave} className="numero">
            <strong>{rs(soma(linhas, p.chave) / 100)}</strong>
          </td>
        ))}
      </tr>
      {aberto &&
        linhas.map((l) => (
          <tr key={l.chave} className={l.semClassificacao ? 'fluxo-sem-classificacao' : undefined} data-teste={`${teste}-linha-${l.chave}`}>
            <td className="fluxo-rotulo">
              {aoAbrir ? (
                <button type="button" className="botao-link" onClick={() => aoAbrir(l.chave, null)} data-teste={`${teste}-abrir-${l.chave}`}>
                  {l.rotulo}
                </button>
              ) : (
                l.rotulo
              )}
              {l.marca}
            </td>
            {vis.map((p) => (
              <td key={p.chave} className="numero">
                {c$(l.porPeriodo[p.chave]) === 0 ? (
                  <span className="texto-secundario">—</span>
                ) : aoAbrir ? (
                  <button type="button" className="botao-link" onClick={() => aoAbrir(l.chave, p.chave)}>
                    {rs(l.porPeriodo[p.chave])}
                  </button>
                ) : (
                  rs(l.porPeriodo[p.chave])
                )}
                {c$(l.provisorioPorPeriodo?.[p.chave]) !== 0 && <div className="texto-secundario conc-mini">{l.rotuloProvisorio ?? 'inclui'} {rs(l.provisorioPorPeriodo![p.chave])} {l.rotuloProvisorio ? '' : 'na lacuna'}</div>}
              </td>
            ))}
          </tr>
        ))}
    </>
  );
}

/* ---------------------------------------------------------------- detalhe até as origens */
function DetalheLinha({ linha, periodo, aoFechar }: { linha: Linha; periodo: string | null; aoFechar: () => void }) {
  const itens = linha.itens.filter((i) => !periodo || i.data.startsWith(periodo));
  return (
    <section className="fluxo-detalhe" data-teste="detalhe-linha">
      <div className="barra-filtros conc-barra">
        <h2 className="conc-titulo">
          {linha.rotulo}
          {periodo ? ` · ${rotuloPeriodo(periodo)}` : ' · todo o período'} · {plural(itens.length, 'movimento', 'movimentos')} · {rs(itens.reduce((s, i) => s + c$(i.valor), 0) / 100)}
        </h2>
        <span className="conc-espaco" />
        <button type="button" className="botao-secundario pequeno" onClick={aoFechar} data-teste="fechar-detalhe-linha">
          Fechar detalhe
        </button>
      </div>
      {linha.restrito && <p className="texto-secundario">Detalhe de origem sem acesso para o seu perfil: aparecem só os dados do extrato.</p>}
      <RolagemIndicada className="rolagem quadro-tabela">
        <table className="tabela tabela-compacta" data-teste="tabela-detalhe-linha">
          <thead>
            <tr>
              <th>Data</th>
              <th>Movimento do extrato</th>
              <th className="numero">Valor</th>
              <th>Origem</th>
            </tr>
          </thead>
          <tbody>
            {itens.map((i, k) => (
              <tr key={`${i.movimento}-${k}`} data-teste={`detalhe-${i.movimento}`}>
                <td className="conc-nowrap">{dataBr(i.data)}</td>
                <td>
                  {i.descricao}
                  <div className="texto-secundario conc-mini">
                    <Codigo>{i.movimento}</Codigo> · {i.conta}
                  </div>
                </td>
                <td className="numero conc-nowrap">{rs(i.valor)}</td>
                <td>
                  {i.fonte === 'hub_lacuna' ? (
                    <>
                      <SeloSituacao texto={i.descricao.includes('aguardando') ? 'Aguardando extrato' : 'Na lacuna · sem extrato'} tom="parcial" />
                      {i.origens.map((o) => (
                        <div key={o} className="conc-mini">
                          <ComCodigos texto={o} />
                        </div>
                      ))}
                    </>
                  ) : i.conciliacao ? (
                    <>
                      <Codigo>{i.conciliacao}</Codigo>
                      {i.origens.map((o) => (
                        <div key={o} className="conc-mini">
                          <ComCodigos texto={o} />
                        </div>
                      ))}
                    </>
                  ) : (
                    <span className="texto-secundario">sem conciliação{i.sugestao ? ` · ${i.sugestao}` : ''}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </RolagemIndicada>
    </section>
  );
}

/* ---------------------------------------------------------------- cronologia da conta (FC-07) */
const TOM_CRONOLOGIA: Record<Cronologia['tom'], 'positivo' | 'aberto' | 'parcial' | 'alerta' | 'encerrado'> = { conferido: 'positivo', conciliado: 'positivo', pendente: 'encerrado', provisorio: 'parcial', divergencia: 'alerta', sugestao: 'parcial' };
const FONTE: Record<Cronologia['fonte'], string> = { saldo_inicial: 'saldo inicial', extrato: 'extrato', saldo_observado: 'saldo observado', hub_provisorio: 'HUB, provisório', hub: 'HUB', saldo_realizado: 'saldo realizado' };
function CronologiaConta({ conta }: { conta: ContaRealizado }) {
  const cob = conta.cobertura;
  const ultimaConf = conta.conferencias.at(-1);
  const primeiraDif = conta.conferencias.find((f) => f.antesDaLacuna && f.diferenca !== null && c$(f.diferenca) !== 0);
  const naLacuna = conta.cronologia!.filter((x) => x.fonte === 'hub_provisorio').reduce((s, x) => s + c$(x.valor), 0);
  return (
    <section data-teste="cronologia-conta">
      <h2 className="conc-titulo">
        Cronologia da conta · {conta.conta.rotulo}
        <Ajuda titulo="Cronologia">Uma fonte de valor por dia: nos dias cobertos, só o extrato; nos dias da lacuna, os fatos do HUB entram provisoriamente. Fato do HUB em dia coberto sem movimento é divergência, fora dos totais. Correspondência provável é só “a confirmar”: nada é conciliado sozinho.</Ajuda>
      </h2>
      <p className="texto-secundario">
        Cobertura efetiva: {cob.cobertos.length ? cob.cobertos.map((x) => `${dataBr(x.de)}–${dataBr(x.ate)}`).join(' e ') : 'nenhuma'}
        {cob.lacunas.length > 0 && ` · Lacuna: ${cob.lacunas.map((x) => (x.de === x.ate ? dataBr(x.de) : `${dataBr(x.de)}–${dataBr(x.ate)}`)).join(', ')}`}
        {cob.canceladas.length > 0 && ` · Cancelada (não cobre): ${cob.canceladas.map((x) => `${x.numero} ${dataBr(x.de).slice(0, 5)}–${dataBr(x.ate).slice(0, 5)}`).join(', ')}`}
      </p>
      <RolagemIndicada className="rolagem quadro-tabela">
        <table className="tabela tabela-compacta" data-teste="tabela-cronologia">
          <thead>
            <tr>
              <th>Data</th>
              <th>Fonte</th>
              <th>Registro</th>
              <th className="numero">Valor</th>
              <th>Situação</th>
            </tr>
          </thead>
          <tbody>
            {conta.cronologia!.map((x, k) => (
              <tr key={k} className={x.foraDosTotais ? 'fluxo-fora' : x.fonte === 'hub_provisorio' ? 'fluxo-provisorio' : x.fonte.startsWith('saldo') ? 'fluxo-saldo' : undefined} data-teste={`cron-${x.fonte}-${k}`}>
                <td className="conc-nowrap">{dataBr(x.data).slice(0, 5)}</td>
                <td>{FONTE[x.fonte]}</td>
                <td>
                  <ComCodigos texto={x.registro} />
                </td>
                <td className="numero conc-nowrap">{x.valor === null ? '—' : x.foraDosTotais ? `(${rs(x.valor)})` : rs(x.valor)}</td>
                <td>
                  <SeloSituacao texto={x.situacao} tom={TOM_CRONOLOGIA[x.tom]} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </RolagemIndicada>
      <CardsLista n={4} teste="cards-cronologia">
        <CardLista
          icone="relogio"
          tom="verde"
          rotulo="Saldo conferido"
          valor={cob.conferidoAte ? `só até ${dataBr(cob.conferidoAte)}` : '—'}
          detalhe={[primeiraDif ? `em ${dataBr(primeiraDif.data)} o banco difere do calculado (${rs(primeiraDif.diferenca!)})` : '', cob.primeiraLacuna ? 'depois da lacuna não há saldo de abertura do banco' : 'sem lacuna no período'].filter(Boolean).join(' · ')}
          teste="card-conferido-ate"
        />
        <CardLista icone="documento" tom="azul" rotulo={`Saldo do extrato${ultimaConf ? ` em ${dataBr(ultimaConf.data).slice(0, 5)}` : ''}`} valor={ultimaConf ? rs(ultimaConf.extrato) : '—'} detalhe={ultimaConf ? `informado pelo banco no ${ultimaConf.importacao}` : 'nenhum saldo informado no período'} teste="card-saldo-extrato-fluxo" />
        <CardLista icone="dinheiro" tom="roxo" rotulo="Calculado só com o importado" valor={ultimaConf?.calculado ? rs(ultimaConf.calculado) : '—'} detalhe={ultimaConf?.diferenca && c$(ultimaConf.diferenca) ? `diferença de ${rs(ultimaConf.diferenca)} sem causa presumida` : ultimaConf ? 'sem diferença' : ''} teste="card-so-importado" />
        <CardLista icone="alerta" tom="vermelho" rotulo="Fatos do HUB na lacuna" valor={rs(naLacuna / 100)} detalhe="coincidir não prova nada: pode haver outros movimentos" teste="card-fatos-lacuna" />
      </CardsLista>
    </section>
  );
}

/* ---------------------------------------------------------------- E3 · projeção atual (FC-03) */
interface ItemProjetado {
  chave: string; grupo: 'entrada' | 'saida'; origem: string; numero: string; obrigacao: string | null; rota: string; descricao: string; data: string; valor: string;
  estado: 'confirmado' | 'estimado'; base: string; marcas: string[]; vencido: boolean; dataOriginal: string | null;
  reprogramacoes: { anterior: string; novo: string; motivo: string | null; em: string; por: string | null; fonte: 'registro' | 'auditoria' }[];
}
interface PendenciaProjecao { tipo: string; grupo: 'entrada' | 'saida'; numero: string; rota: string; descricao: string; falta: string; valor: string | null; ondeResolver: string }
interface Projetado {
  de: string; ate: string; hoje: string; granularidade: 'dia' | 'mes';
  acesso: { conciliacao: boolean; pagar: boolean; receber: boolean; parcial: boolean };
  partida: { valor: string | null; provisorio: boolean; posicaoInvestida: string | null; fatosNaLacuna: string };
  totais: { entradas: string; entradasConfirmadas: string; entradasEstimadas: string; saidas: string; saidasConfirmadas: string; saidasEstimadas: string; saidasVencidas: string; entradasVencidas: string; saldoPrevisto: string | null; seEntradasVencidasRecebidas: string | null; pendencias: number; depoisDoPeriodo: number };
  periodos: { chave: string; de: string; ate: string; entradasConfirmadas: string; entradasEstimadas: string; saidasConfirmadas: string; saidasEstimadas: string; saidasVencidasDescontadas: string; saldoInicial: string | null; saldoFinal: string | null; seEntradasVencidasRecebidas: string | null }[];
  foraDaJanela: { itens: number; entradas: string; saidas: string; primeiraData: string | null; ultimaData: string | null; lista: ItemProjetado[] };
  vencidos: ItemProjetado[];
  itens: ItemProjetado[];
  pendencias: PendenciaProjecao[];
  conciliacaoReceber: { linhas: { rotulo: string; valor: string }[]; semValor: number; confere: boolean } | null;
  avisoRecorrencias: string | null;
}
const diasDesde = (d: string, hoje: string) => Math.round((Date.parse(`${hoje}T00:00:00Z`) - Date.parse(`${d}T00:00:00Z`)) / 86_400_000);
const valorSinal = (v: string) => <span className={Number(v) < 0 ? 'conc-saida' : 'conc-entrada'}>{Number(v) > 0 ? '+' : ''}{rs(v)}</span>;

function AbaProjetado() {
  const { pode } = useSessao();
  const hoje = hojeIso();
  const [granularidade, setGranularidade] = useState<'mes' | 'dia'>('mes');
  // D13: horizonte de 12 meses (o atual + 11) na visão mensal e de 90 dias na diária
  const mesMax = somarMeses(hoje.slice(0, 7), 11);
  const diaMax = somarDias(hoje, 89);
  const [mesFim, setMesFim] = useState(mesMax);
  const [diaFim, setDiaFim] = useState(diaMax);
  const [verFora, setVerFora] = useState(false);
  const [so, setSo] = useState<'' | 'confirmado' | 'estimado'>('');
  const [reprogramar, setReprogramar] = useState<ItemProjetado | null>(null);
  const ate = granularidade === 'mes' ? ultimoDia(mesFim) : diaFim;
  const carga = useCarga(() => api.get<Projetado>(`/api/fluxo-caixa/projetado?granularidade=${granularidade}&ate=${ate}`), [granularidade, ate]);
  const d = carga.dados;
  const filtros = (
    <div className="barra-filtros conc-barra" data-teste="filtros-projecao">
      <label>
        Visão
        <select value={granularidade} onChange={(e) => setGranularidade(e.target.value as 'mes' | 'dia')} data-teste="granularidade-projecao">
          <option value="mes">Mensal</option>
          <option value="dia">Diária</option>
        </select>
      </label>
      {granularidade === 'mes' ? (
        <label>
          Até
          <input type="month" value={mesFim} min={hoje.slice(0, 7)} max={mesMax} onChange={(e) => e.target.value && setMesFim(e.target.value > mesMax ? mesMax : e.target.value)} data-teste="mes-fim-projecao" />
        </label>
      ) : (
        <label>
          Até
          <input type="date" value={diaFim} min={hoje} max={diaMax} onChange={(e) => e.target.value && setDiaFim(e.target.value > diaMax ? diaMax : e.target.value)} data-teste="dia-fim-projecao" />
        </label>
      )}
      <label>
        Mostrar
        <select value={so} onChange={(e) => setSo(e.target.value as '' | 'confirmado' | 'estimado')} data-teste="estado-projecao">
          <option value="">Confirmados e estimados</option>
          <option value="confirmado">Só confirmados</option>
          <option value="estimado">Só estimados</option>
        </select>
      </label>
    </div>
  );
  if (!d)
    return (
      <>
        {filtros}
        <EstadoCarga carregando={carga.carregando} erro={carga.erro} recarregar={carga.recarregar} teste="fluxo-projetado" />
      </>
    );
  const t = d.totais;
  const filtra = (xs: ItemProjetado[]) => xs.filter((i) => !so || i.estado === so);
  const podeReprogramar = pode('editar', 'conta_pagar');
  return (
    <>
      {filtros}
      {d.acesso.parcial && <AvisoParcial texto={!d.acesso.conciliacao ? 'O saldo de partida vem do extrato (Conciliação), que o seu perfil não vê: a projeção mostra só a variação prevista das partes visíveis.' : undefined} />}
      <CardsLista n={4} teste="cards-projecao">
        <CardLista
          icone="dinheiro"
          tom="azul"
          rotulo={`Saldo de partida · hoje ${dataBr(d.hoje).slice(0, 5)}`}
          valor={d.partida.valor === null ? '—' : rs(d.partida.valor)}
 detalhe={d.partida.valor === null ? (d.acesso.conciliacao ? 'nenhuma conta com saldo inicial' : 'sem acesso ao realizado') : `realizado até hoje${d.partida.provisorio ? ' · provisório' : ''}${c$(d.partida.fatosNaLacuna) ? ` · inclui ${rs(d.partida.fatosNaLacuna)} sem extrato` : ''}${d.partida.posicaoInvestida ? ` · investido à parte ${rs(d.partida.posicaoInvestida)}` : ''}`}
          teste="card-partida"
        />
        <CardLista icone="documento" tom="verde" rotulo={`Entradas do período (${dataBr(d.de).slice(0, 5)} → ${dataBr(d.ate).slice(0, 5)})`} valor={rs(t.entradas)} detalhe={`confirmadas ${rs(t.entradasConfirmadas)} · estimadas ${rs(t.entradasEstimadas)}`} teste="card-entradas-projecao" />
        <CardLista icone="relogio" tom="vermelho" rotulo="Saídas do período" valor={rs(Math.abs(c$(t.saidas)) / 100)} detalhe={`confirmadas ${rs(Math.abs(c$(t.saidasConfirmadas)) / 100)} · estimadas ${rs(Math.abs(c$(t.saidasEstimadas)) / 100)} · + vencidas ${rs(Math.abs(c$(t.saidasVencidas)) / 100)}`} teste="card-saidas-projecao" />
        <CardLista
          icone="alerta"
          tom="roxo"
          rotulo={`Saldo previsto · ${dataBr(d.ate).slice(0, 5)}`}
          valor={t.saldoPrevisto === null ? '—' : rs(t.saldoPrevisto)}
          detalhe={t.saldoPrevisto === null ? 'sem saldo de partida' : `com as saídas vencidas descontadas (${rs(t.saidasVencidas)}) · se receber as entradas vencidas: ${rs(t.seEntradasVencidasRecebidas)}`}
          ajuda={<Ajuda titulo="Saldo previsto">Partida + entradas − saídas do período − saídas vencidas ainda devidas (pelo saldo em aberto: o já pago não é descontado de novo). As entradas vencidas ficam fora, com o cenário “se recebidas”. Os vencidos ficam no grupo próprio, na data original; nada é empurrado de data sozinho: reprogramar é uma ação com motivo.</Ajuda>}
          teste="card-saldo-previsto"
        />
      </CardsLista>
      {d.avisoRecorrencias && <p className="aviso aviso-atencao-fin">{d.avisoRecorrencias}</p>}
      <RolagemIndicada className="rolagem quadro-tabela">
        <table className="tabela tabela-compacta fluxo-projecao" data-teste="tabela-projecao">
          <thead>
            <tr>
              <th>Data</th>
              <th>Origem</th>
              <th>Descrição</th>
              <th className="numero">Valor previsto</th>
              <th>Base do valor</th>
              <th>Projeção</th>
            </tr>
          </thead>
          <tbody>
            <tr className="fluxo-grupo">
              <td colSpan={6}>
                <strong>Vencidos (data original preservada)</strong>
              </td>
            </tr>
            {filtra(d.vencidos).length ? (
              filtra(d.vencidos).map((i) => <LinhaProjecao key={i.chave} i={i} hoje={d.hoje} podeReprogramar={podeReprogramar} aoReprogramar={setReprogramar} />)
            ) : (
              <tr>
                <td colSpan={6} className="texto-secundario">
                  Nenhum vencido{so ? ' neste filtro' : ''}.
                </td>
              </tr>
            )}
            <tr className="fluxo-grupo">
              <td colSpan={6}>
                <strong>No período</strong>
              </td>
            </tr>
            {filtra(d.itens).map((i) => (
              <LinhaProjecao key={i.chave} i={i} hoje={d.hoje} podeReprogramar={podeReprogramar} aoReprogramar={setReprogramar} />
            ))}
            <tr className="fluxo-saldo" data-teste="linha-saldo-previsto">
              <td colSpan={3}>Saldo previsto em {dataBr(d.ate)} (saídas vencidas descontadas)</td>
              <td className="numero">{t.saldoPrevisto === null ? '—' : rs(t.saldoPrevisto)}</td>
              <td colSpan={2} className="texto-secundario conc-mini">
                {d.partida.valor === null ? 'variação do período' : `${rs(d.partida.valor)} + ${rs(t.entradas)} − ${rs(Math.abs(c$(t.saidas)) / 100)} − vencidas ${rs(Math.abs(c$(t.saidasVencidas)) / 100)}`}
              </td>
            </tr>
            <tr data-teste="linha-cenario-entradas">
              <td colSpan={3} className="texto-secundario">se as entradas vencidas forem recebidas</td>
              <td className="numero">{t.seEntradasVencidasRecebidas === null ? '—' : rs(t.seEntradasVencidasRecebidas)}</td>
              <td colSpan={2} />
            </tr>
          </tbody>
        </table>
      </RolagemIndicada>
      {d.periodos.length > 1 && (
        <>
          <h2 className="conc-titulo">Resumo por {d.granularidade === 'mes' ? 'mês' : 'dia'}</h2>
          <MatrizFluxo
            teste="matriz-projecao"
            periodos={d.periodos.map((p) => ({ chave: p.chave, saldoInicial: p.saldoInicial, saldoFinal: p.saldoFinal, provisorio: false, movimento: '0', posicaoInvestida: null, posicaoProvisoria: false }))}
            linhas={[
              { chave: 'ent-conf', grupo: 'entrada', rotulo: 'Entradas confirmadas', porPeriodo: Object.fromEntries(d.periodos.map((p) => [p.chave, p.entradasConfirmadas])), total: t.entradasConfirmadas },
              { chave: 'ent-est', grupo: 'entrada', rotulo: 'Entradas estimadas', porPeriodo: Object.fromEntries(d.periodos.map((p) => [p.chave, p.entradasEstimadas])), total: t.entradasEstimadas },
              { chave: 'sai-conf', grupo: 'saida', rotulo: 'Saídas confirmadas', porPeriodo: Object.fromEntries(d.periodos.map((p) => [p.chave, p.saidasConfirmadas])), total: t.saidasConfirmadas },
              { chave: 'sai-est', grupo: 'saida', rotulo: 'Saídas estimadas', porPeriodo: Object.fromEntries(d.periodos.map((p) => [p.chave, p.saidasEstimadas])), total: t.saidasEstimadas },
            ]}
            rotuloSaldo="Saldo previsto (saídas vencidas descontadas no 1º período)"
            semInvestida
          />
        </>
      )}
      {d.foraDaJanela.itens > 0 && (
        <section className="aviso aviso-atencao-fin" data-teste="fora-da-janela">
          <strong>Fora da janela:</strong> {plural(d.foraDaJanela.itens, 'item conhecido', 'itens conhecidos')} depois de {dataBr(d.ate)} (de {dataBr(d.foraDaJanela.primeiraData)} a {dataBr(d.foraDaJanela.ultimaData)}): entradas {rs(d.foraDaJanela.entradas)} · saídas {rs(Math.abs(c$(d.foraDaJanela.saidas)) / 100)}. Não entram no saldo desta janela, mas não somem.{' '}
          <button type="button" className="botao-link" onClick={() => setVerFora(!verFora)} data-teste="ver-fora-da-janela">
            {verFora ? 'Ocultar origens' : 'Ver as origens'}
          </button>
          {verFora && (
            <ul className="conc-efeitos" data-teste="lista-fora-da-janela">
              {d.foraDaJanela.lista.map((i) => (
                <li key={i.chave}>
                  {dataBr(i.data)} · <a href={`#${i.rota}`}>{i.numero}</a> · {i.descricao} · {valorSinal(i.valor)} · {i.estado}
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
      <h2 className="conc-titulo">
        Pendências da projeção (fora dos totais)
        <Ajuda titulo="Pendências">Valor ou data desconhecidos nunca entram como zero nem com data inventada: ficam aqui, com o que falta e onde resolver. Parcela de compra no cartão sem fatura vai para a fatura prevista; só fica aqui se faltar dado do cartão para calcular.</Ajuda>
      </h2>
      {d.pendencias.length ? (
        <RolagemIndicada className="rolagem quadro-tabela">
          <table className="tabela tabela-compacta" data-teste="tabela-pendencias-projecao">
            <thead>
              <tr>
                <th>Origem</th>
                <th>O que falta</th>
                <th className="numero">Valor conhecido</th>
                <th>Onde resolver</th>
              </tr>
            </thead>
            <tbody>
              {d.pendencias.map((p, k) => (
                <tr key={`${p.numero}-${k}`} data-teste={`pend-${p.tipo}-${p.numero}`}>
                  <td>
                    <a href={`#${p.rota}`}>{p.numero}</a>
                    <div className="texto-secundario conc-mini">{p.descricao}</div>
                  </td>
                  <td>{p.falta}</td>
                  <td className="numero conc-nowrap">{p.valor === null ? 'desconhecido' : valorSinal(p.valor)}</td>
                  <td className="texto-secundario">{p.ondeResolver}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </RolagemIndicada>
      ) : (
        <p className="texto-secundario">Nenhuma pendência{d.acesso.parcial ? ' entre o que o seu perfil vê' : ''}.</p>
      )}
      {d.conciliacaoReceber && (
        <>
          <h2 className="conc-titulo">
            Contas a Receber × Fluxo
            <Ajuda titulo="Receber × Fluxo">O “Previsto a receber” do Contas a Receber é a carteira inteira (inclui vencidas, sem data e IR das notas). O Fluxo projeta o líquido de cada parcela na data esperada do período. A diferença é explicada linha a linha.</Ajuda>
          </h2>
          <ul className="conc-efeitos" data-teste="conciliacao-receber">
            {d.conciliacaoReceber.linhas.map((l) => (
              <li key={l.rotulo}>
                {l.rotulo}: <strong>{rs(l.valor)}</strong>
              </li>
            ))}
            {d.conciliacaoReceber.semValor > 0 && <li className="texto-secundario">+ {plural(d.conciliacaoReceber.semValor, 'parcela sem estimativa', 'parcelas sem estimativa')} (valor desconhecido, fora das somas)</li>}
            <li className={d.conciliacaoReceber.confere ? 'conc-ok' : 'conc-dif'}>{d.conciliacaoReceber.confere ? 'confere ✓' : 'não confere: verificar'}</li>
          </ul>
        </>
      )}
      {reprogramar && <ReprogramarVencimento obrigacao={reprogramar.obrigacao!} vencimento={reprogramar.data} descricao={`${reprogramar.numero} · ${reprogramar.descricao}`} aoFechar={() => setReprogramar(null)} aoConcluir={() => (setReprogramar(null), void carga.recarregar())} />}
    </>
  );
}

function LinhaProjecao({ i, hoje, podeReprogramar, aoReprogramar }: { i: ItemProjetado; hoje: string; podeReprogramar: boolean; aoReprogramar: (i: ItemProjetado) => void }) {
  const atraso = i.vencido ? diasDesde(i.data, hoje) : 0;
  return (
    <tr data-teste={`proj-${i.numero}`} className={i.vencido ? 'fluxo-vencido' : undefined}>
      <td className="conc-nowrap">{dataBr(i.data).slice(0, 5)}</td>
      <td className="conc-nowrap">
        <a href={`#${i.rota}`}>{i.numero}</a>
      </td>
      <td>
        {i.descricao}
        {i.dataOriginal && i.dataOriginal !== i.data && <div className="texto-secundario conc-mini">original {dataBr(i.dataOriginal)}{i.reprogramacoes.length ? ` · ${plural(i.reprogramacoes.length, 'reprogramação', 'reprogramações')}` : ''}</div>}
        {i.reprogramacoes.slice(-1).map((r) => (
          <div key={r.em} className="texto-secundario conc-mini">
            {r.anterior ? `${dataBr(r.anterior).slice(0, 5)} → ` : ''}{dataBr(r.novo).slice(0, 5)}{r.motivo ? ` · “${r.motivo}”` : ''}{r.fonte === 'auditoria' ? ' · da auditoria' : ''}
          </div>
        ))}
      </td>
      <td className="numero conc-nowrap">{i.vencido && i.grupo === 'entrada' ? <s>{valorSinal(i.valor)}</s> : valorSinal(i.valor)}</td>
      <td className="texto-secundario conc-mini">{[i.base, ...i.marcas.filter((m) => !m.startsWith('confirmado'))].join(' · ')}</td>
      <td>
        <span className="conc-acoes-pilha">
          {i.vencido && <SeloSituacao texto={`${i.grupo === 'entrada' ? 'vencida' : 'vencido'} há ${atraso} ${atraso === 1 ? 'dia' : 'dias'}`} tom="alerta" />}
          {i.vencido && <SeloSituacao texto={i.grupo === 'entrada' ? 'fora do saldo previsto' : 'descontado do saldo previsto'} tom="parcial" />}
          {!i.vencido && <SeloSituacao texto={i.marcas.includes('confirmado · líquido') ? 'confirmado · líquido' : i.estado} tom={i.estado === 'confirmado' ? 'positivo' : 'aberto'} />}
          {i.grupo === 'saida' && i.obrigacao && podeReprogramar && (
            <button type="button" className="botao-link" onClick={() => aoReprogramar(i)} data-teste={`reprogramar-${i.numero}`}>
              Reprogramar
            </button>
          )}
        </span>
      </td>
    </tr>
  );
}


/* ---------------------------------------------------------------- E4 · fotos da projeção (FC-05, FC-01) */
interface FotoResumo { numero: string; capturadaEm: string; cobre: { de: string; ate: string }; modo: 'manual' | 'automatica'; por: string | null; motivo: string | null; saldoPrevisto: string | null; itens: number; vencidos: number; parcial: boolean }
interface ListaFotos { fotos: FotoResumo[]; podeGerar: boolean }
interface LinhaComparacao { rotulo: string; grupo: 'entrada' | 'saida'; fotoConfirmado: string | null; fotoEstimado: string | null; realizado: string | null; diferenca: string | null; explicacao: string[]; marca: string | null }
interface ItemComparado extends ItemProjetado { desfecho: { situacao: string; texto: string; realizado: string } | null }
interface Comparacao {
  foto: { numero: string; capturadaEm: string; cobre: { de: string; ate: string }; modo: string; por: string | null; motivo: string | null; partida: { valor: string | null; provisorio: boolean; posicaoInvestida: string | null } | null; saldoPrevisto: string | null };
  janela: { diaCaptura: string; de: string; ate: string; parcialAteHoje: boolean; vazia: boolean; regra: string };
  diaDaCaptura: { data: string; previstos: ItemComparado[]; realizado: { entradas: string; saidas: string; itens: number } | null };
  lancadosDepoisComDataAnterior: { numero: string; data: string; valor: string }[];
  foraDaFoto: { entrada?: { itens: number; valor: string }; saida?: { itens: number; valor: string }; ate: string } | null;
  acesso: { parcial: boolean };
  linhas: LinhaComparacao[];
  saldoRealizado: string | null;
  saldoRealizadoProvisorio: boolean | null;
  antesDaFoto: { de: string; ate: string; entradas: string; saidas: string } | null;
  itens: ItemComparado[];
  vencidosDaFoto: ItemComparado[];
  depoisDaJanela: ItemComparado[];
  pendenciasDaFoto: { tipo: string; numero: string; descricao: string; falta: string; valor: string | null }[];
}
const quando = (iso: string) => new Date(iso).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }).replace(',', '');
const TOM_DESFECHO: Record<string, 'positivo' | 'parcial' | 'alerta' | 'aberto' | 'encerrado'> = { realizado: 'positivo', parcial: 'parcial', reprogramado: 'parcial', em_aberto: 'aberto', vencido: 'alerta', cancelado: 'encerrado', fora_da_projecao: 'encerrado' };

function AbaFotos() {
  const hoje = hojeIso();
  const lista = useCarga(() => api.get<ListaFotos>('/api/fluxo-caixa/fotos'), []);
  const [aberta, setAberta] = useState<string | null>(null);
  const [gerar, setGerar] = useState(false);
  const d = lista.dados;
  if (!d) return <EstadoCarga carregando={lista.carregando} erro={lista.erro} recarregar={lista.recarregar} teste="fotos" />;
  const meses: string[] = [];
  for (let k = 0; k < 6; k++) meses.push(somarMeses(hoje.slice(0, 7), -k));
  for (const f of d.fotos) if (!meses.includes(f.cobre.de.slice(0, 7))) meses.push(f.cobre.de.slice(0, 7));
  meses.sort().reverse();
  return (
    <>
      <div className="barra-filtros conc-barra">
        <p className="texto-secundario fluxo-legenda">
          Três coisas separadas: <strong>projeção atual</strong> (o que segue em aberto, recalculada a cada acesso), <strong>foto</strong> (o que se esperava, com a data e a hora reais da captura; nunca muda) e <strong>realizado</strong>.
        </p>
        <span className="conc-espaco" />
        {d.podeGerar && (
          <button type="button" className="botao-primario" onClick={() => setGerar(true)} data-teste="gerar-foto">
            Gerar foto agora
          </button>
        )}
      </div>
      <RolagemIndicada className="rolagem quadro-tabela">
        <table className="tabela tabela-compacta" data-teste="tabela-fotos">
          <thead>
            <tr>
              <th>Período</th>
              <th>Foto · como</th>
              <th>Capturada em · cobre</th>
              <th>Comparação disponível</th>
            </tr>
          </thead>
          <tbody>
            {meses.map((m) => {
              const fs = d.fotos.filter((f) => f.cobre.de.slice(0, 7) === m);
              if (!fs.length)
                return (
                  <tr key={m} data-teste={`sem-foto-${m}`}>
                    <td className="conc-nowrap">{mesExtenso(m)}</td>
                    <td colSpan={2} className="texto-secundario">
                      —
                    </td>
                    <td>
                      <SeloSituacao texto="sem foto: sem comparação histórica" tom="alerta" />
                    </td>
                  </tr>
                );
              return fs.map((f) => (
                <tr key={f.numero} data-teste={`foto-${f.numero}`}>
                  <td className="conc-nowrap">{mesExtenso(m)}</td>
                  <td>
                    <button type="button" className="botao-link" onClick={() => setAberta(f.numero)} data-teste={`abrir-foto-${f.numero}`}>
                      {f.numero}
                    </button>
                    <div className="texto-secundario conc-mini">
                      {f.modo === 'automatica' ? 'automática' : `manual${f.por ? ` · ${f.por}` : ''}`}
                      {f.motivo ? ` · “${f.motivo}”` : ''}
                    </div>
                  </td>
                  <td>
                    <strong>{quando(f.capturadaEm)}</strong>
                    <div className="texto-secundario conc-mini">
                      cobre {quando(f.capturadaEm).slice(0, 5)} {quando(f.capturadaEm).slice(-5)} → {dataBr(f.cobre.ate).slice(0, 5)} · {f.parcial ? 'leitura parcial (seu perfil)' : `previsto ${f.saldoPrevisto ? rs(f.saldoPrevisto) : '—'}`}
                    </div>
                  </td>
                  <td>
                    {f.cobre.de.slice(8) === '01' ? <SeloSituacao texto="mês inteiro" tom="positivo" /> : <SeloSituacao texto={`parcial: 01/${m.slice(5)} a ${dataBr(f.cobre.de).slice(0, 5)} sem foto`} tom="parcial" />}
                  </td>
                </tr>
              ));
            })}
          </tbody>
        </table>
      </RolagemIndicada>
      {aberta && <ComparacaoFoto numero={aberta} aoFechar={() => setAberta(null)} />}
      {gerar && <GerarFoto aoFechar={() => setGerar(false)} aoConcluir={(n) => (setGerar(false), void lista.recarregar(), setAberta(n))} />}
    </>
  );
}

function ComparacaoFoto({ numero, aoFechar }: { numero: string; aoFechar: () => void }) {
  const carga = useCarga(() => api.get<Comparacao>(`/api/fluxo-caixa/fotos/${numero}/comparacao`), [numero]);
  const c = carga.dados;
  if (!c) return <EstadoCarga carregando={carga.carregando} erro={carga.erro} recarregar={carga.recarregar} teste="comparacao-foto" />;
  const dif = c.saldoRealizado !== null && c.foto.saldoPrevisto !== null && !c.janela.parcialAteHoje ? c$(c.saldoRealizado) - c$(c.foto.saldoPrevisto) : null;
  return (
    <section className="fluxo-detalhe" data-teste="comparacao-foto">
      <div className="barra-filtros conc-barra">
        <h2 className="conc-titulo">
          Foto da projeção {c.foto.numero} × realizado
        </h2>
        <span className="conc-espaco" />
        <button type="button" className="botao-secundario pequeno" onClick={aoFechar}>
          Fechar comparação
        </button>
      </div>
      <p className="faixa-resumo-modal" data-teste="intervalo-comparacao">
        <span>
          capturada em <strong>{quando(c.foto.capturadaEm)}</strong> ({c.foto.modo === 'automatica' ? 'automática' : `manual${c.foto.por ? `, ${c.foto.por}` : ''}`}) · cobre {dataBr(c.foto.cobre.de)} → {dataBr(c.foto.cobre.ate)} ·{' '}
          {c.janela.vazia ? 'a comparação começa no dia seguinte à captura' : `comparação de ${dataBr(c.janela.de)} a ${dataBr(c.janela.ate)}${c.janela.parcialAteHoje ? ' (o período ainda não terminou)' : ''} · mesmo intervalo para previsto e realizado`}
          <Ajuda titulo="Intervalo da comparação">{c.janela.regra}</Ajuda>
        </span>
      </p>
      {c.acesso.parcial && <AvisoParcial />}
      <CardsLista n={4} teste="cards-comparacao">
        <CardLista icone="dinheiro" tom="azul" rotulo="Saldo de partida na foto" valor={c.foto.partida?.valor ? rs(c.foto.partida.valor) : '—'} detalhe={c.foto.partida?.provisorio ? 'provisório na captura' : 'na captura'} teste="card-partida-foto" />
        <CardLista icone="documento" tom="roxo" rotulo={`Saldo previsto na foto · ${dataBr(c.foto.cobre.ate).slice(0, 5)}`} valor={c.foto.saldoPrevisto ? rs(c.foto.saldoPrevisto) : '—'} detalhe="congelado: não muda com pagamentos ou alterações posteriores" teste="card-previsto-foto" />
        <CardLista icone="relogio" tom="verde" rotulo={`Saldo realizado · ${dataBr(c.janela.ate).slice(0, 5)}`} valor={c.saldoRealizado ? rs(c.saldoRealizado) : '—'} detalhe={c.saldoRealizadoProvisorio ? 'provisório (há dias sem extrato)' : 'pelo extrato'} teste="card-realizado-foto" />
        <CardLista icone="alerta" tom="vermelho" rotulo="Diferença (realizado − foto)" valor={dif === null ? '—' : rs(dif / 100)} detalhe={dif === null ? (c.janela.parcialAteHoje ? 'o período ainda não terminou: veja por natureza' : 'sem o saldo das duas pontas') : 'explicada por natureza abaixo'} teste="card-diferenca-foto" />
      </CardsLista>
      <RolagemIndicada className="rolagem quadro-tabela">
        <table className="tabela tabela-compacta" data-teste="tabela-comparacao">
          <thead>
            <tr>
              <th>Natureza</th>
              <th className="numero">Foto: confirmado</th>
              <th className="numero">Foto: estimado</th>
              <th className="numero">Realizado</th>
              <th className="numero">Diferença</th>
              <th>Explicação</th>
            </tr>
          </thead>
          <tbody>
            {(['entrada', 'saida'] as const).map((g) => (
              <Fragmento key={g}>
                <tr className="fluxo-grupo">
                  <td colSpan={6}>
                    <strong>{g === 'entrada' ? 'Entradas' : 'Saídas'}</strong>
                  </td>
                </tr>
                {c.linhas
                  .filter((l) => l.grupo === g)
                  .map((l) => (
                    <tr key={l.rotulo} data-teste={`comp-${l.rotulo}`}>
                      <td>
                        {l.rotulo}
                        {l.marca && <div className="texto-secundario conc-mini">{l.marca}</div>}
                      </td>
                      <td className="numero">{l.fotoConfirmado === null ? '—' : rs(l.fotoConfirmado)}</td>
                      <td className="numero">{l.fotoEstimado === null ? '—' : rs(l.fotoEstimado)}</td>
                      <td className="numero">{l.realizado === null ? '—' : rs(l.realizado)}</td>
                      <td className="numero">{l.diferenca === null ? '—' : valorSinal(l.diferenca)}</td>
                      <td className="conc-mini">
                        {l.explicacao.length ? (
                          l.explicacao.slice(0, 6).map((x) => (
                            <div key={x}>
                              <ComCodigos texto={x} />
                            </div>
                          ))
                        ) : (
                          <span className="texto-secundario">—</span>
                        )}
                        {l.explicacao.length > 6 && <div className="texto-secundario">+ {l.explicacao.length - 6}</div>}
                      </td>
                    </tr>
                  ))}
              </Fragmento>
            ))}
          </tbody>
        </table>
      </RolagemIndicada>
      <div className="aviso aviso-atencao-fin" data-teste="dia-da-captura">
        <strong>Dia da captura ({dataBr(c.diaDaCaptura.data)}) · ordem em relação à hora da captura não comprovada:</strong> fica fora da comparação.{' '}
        {c.diaDaCaptura.previstos.length ? `Previstos na foto para o dia: ${c.diaDaCaptura.previstos.map((i) => `${i.numero} ${rs(i.valor)}${i.desfecho ? ` (${i.desfecho.texto})` : ''}`).join('; ')}.` : 'Nenhum previsto na foto para o dia.'}{' '}
        {c.diaDaCaptura.realizado ? `Realizado no dia: entradas ${rs(c.diaDaCaptura.realizado.entradas)} · saídas ${rs(c.diaDaCaptura.realizado.saidas)} (${plural(c.diaDaCaptura.realizado.itens, 'item', 'itens')}).` : ''}
      </div>
      {c.lancadosDepoisComDataAnterior.length > 0 && (
        <p className="texto-secundario" data-teste="lancados-depois">
          Lançados depois da captura com data efetiva anterior ao dia da captura (fora da comparação; a foto não podia conhecê-los): {c.lancadosDepoisComDataAnterior.map((x) => `${x.numero} ${rs(x.valor)} em ${dataBr(x.data)}`).join('; ')}.
        </p>
      )}
      {c.foraDaFoto && (c.foraDaFoto.entrada?.itens || c.foraDaFoto.saida?.itens) ? (
        <p className="texto-secundario" data-teste="fora-da-foto">
          Já conhecidos na captura para depois de {dataBr(c.foraDaFoto.ate)} (fora da foto e da comparação):{' '}
          {[c.foraDaFoto.entrada?.itens ? `${plural(c.foraDaFoto.entrada.itens, 'entrada', 'entradas')} ${rs(c.foraDaFoto.entrada.valor)}` : '', c.foraDaFoto.saida?.itens ? `${plural(c.foraDaFoto.saida.itens, 'saída', 'saídas')} ${rs(c.foraDaFoto.saida.valor)}` : ''].filter(Boolean).join(' · ')}.
        </p>
      ) : null}
      {c.antesDaFoto && (
        <p className="aviso aviso-atencao-fin" data-teste="antes-da-foto">
          Antes da foto ({dataBr(c.antesDaFoto.de)} a {dataBr(c.antesDaFoto.ate)}): entradas {rs(c.antesDaFoto.entradas)} · saídas {rs(c.antesDaFoto.saidas)} — ocorreram antes da captura e ficam <strong>fora da comparação</strong>.
        </p>
      )}
      <h3 className="conc-titulo">Itens da foto e o que aconteceu com cada um</h3>
      <RolagemIndicada className="rolagem quadro-tabela">
        <table className="tabela tabela-compacta" data-teste="tabela-itens-foto">
          <thead>
            <tr>
              <th>Origem</th>
              <th>Na foto</th>
              <th>Desfecho</th>
            </tr>
          </thead>
          <tbody>
            {[...c.vencidosDaFoto, ...c.itens, ...c.depoisDaJanela].map((i) => (
              <tr key={i.chave} data-teste={`item-foto-${i.numero}`}>
                <td className="conc-nowrap">
                  <a href={`#${i.rota ?? ''}`}>{i.numero}</a>
                </td>
                <td>
                  {dataBr(i.data).slice(0, 5)} · {valorSinal(i.valor)} · {i.estado}
                  {i.vencido ? ' · vencido na captura' : ''}
                  {i.data > c.janela.ate ? ' · previsto para depois de hoje' : ''}
                  <div className="texto-secundario conc-mini">{i.descricao}</div>
                </td>
                <td>{i.desfecho ? <SeloSituacao texto={i.desfecho.texto} tom={TOM_DESFECHO[i.desfecho.situacao] ?? 'aberto'} /> : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </RolagemIndicada>
      {c.depoisDaJanela.length > 0 && <p className="texto-secundario">{plural(c.depoisDaJanela.length, 'item da foto previsto', 'itens da foto previstos')} para depois de hoje: entram na comparação por natureza quando a data chegar; o desfecho (inclusive pagamento antecipado) já aparece acima.</p>}
      {c.pendenciasDaFoto.length > 0 && <p className="texto-secundario">Na captura, {plural(c.pendenciasDaFoto.length, 'pendência ficava', 'pendências ficavam')} fora dos totais (sem data ou valor desconhecido).</p>}
    </section>
  );
}
const Fragmento = ({ children }: { children: ReactNode }) => <>{children}</>;

function GerarFoto({ aoFechar, aoConcluir }: { aoFechar: () => void; aoConcluir: (numero: string) => void }) {
  const [motivo, setMotivo] = useState('');
  const [previa, setPrevia] = useState<{ numero: string; cobre: { de: string; ate: string }; saldoPrevisto: string | null; itens: number; vencidos: number; pendencias: number } | null>(null);
  const [erro, setErro] = useState('');
  const [enviando, setEnviando] = useState(false);
  useEffect(() => setPrevia(null), [motivo]);
  const enviar = async (comPrevia: boolean) => {
    setEnviando(true);
    setErro('');
    try {
      const r = await api.post<{ numero: string; cobre: { de: string; ate: string }; saldoPrevisto: string | null; itens: number; vencidos: number; pendencias: number }>('/api/fluxo-caixa/fotos', { motivo: motivo.trim() || undefined, previa: comPrevia });
      if (comPrevia) setPrevia(r);
      else aoConcluir(r.numero);
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setEnviando(false);
    }
  };
  return (
    <Modal
      titulo="Gerar foto da projeção agora"
      aoFechar={aoFechar}
      medio
      rodape={
        <>
          <span className="texto-secundario conc-rodape-efeito">{previa ? 'Confira e confirme.' : 'Nada é gravado até confirmar.'}</span>
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Voltar
          </button>
          {previa ? (
            <button type="button" className="botao-primario" disabled={enviando} onClick={() => void enviar(false)} data-teste="confirmar-foto">
              Gerar foto
            </button>
          ) : (
            <button type="button" className="botao-primario" disabled={enviando} onClick={() => void enviar(true)} data-teste="previa-foto">
              Ver prévia
            </button>
          )}
        </>
      }
    >
      <div data-teste="modal-gerar-foto">
        <p className="texto-secundario">A foto registra o que se espera agora, com a data e a hora reais, do dia de hoje até o fim do mês. Ela nunca muda: pagamentos, reprogramações e reaberturas posteriores não a alteram.</p>
        <Campo rotulo="Motivo (opcional)">{(id) => <input id={id} maxLength={500} value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Ex.: expectativa depois da renegociação" data-teste="motivo-foto" />}</Campo>
        {previa && (
          <p className="faixa-resumo-modal" data-teste="previa-gerar-foto">
            <span>
              Cobre {dataBr(previa.cobre.de)} → {dataBr(previa.cobre.ate)} · saldo previsto {previa.saldoPrevisto ? rs(previa.saldoPrevisto) : '—'} · {plural(previa.itens, 'item', 'itens')} · {plural(previa.vencidos, 'vencido', 'vencidos')} · {plural(previa.pendencias, 'pendência', 'pendências')} (prévia, nada gravado)
            </span>
          </p>
        )}
        {erro && (
          <p className="aviso aviso-erro" role="alert">
            {erro}
          </p>
        )}
      </div>
    </Modal>
  );
}
